import { ElectricalSymbol, SymbolCategory, SymbolCategoryInfo, SymbolSubcategory, SymbolSubcategoryInfo, SymbolType } from '@/types/electrical';

export const symbolCategories: SymbolCategoryInfo[] = [
  { id: 'lighting', name: 'Belysning', icon: 'lightbulb' },
  { id: 'switches', name: 'Afbrydere & Betjening', icon: 'toggle-left' },
  { id: 'outlets', name: 'Tilslutninger', icon: 'plug' },
  { id: 'data', name: 'Data & Kommunikation', icon: 'wifi' },
  { id: 'power', name: 'Forsyning & Installationer', icon: 'zap' },
  { id: 'safety', name: 'Sikkerhed', icon: 'shield' },
  { id: 'aia', name: 'AIA', icon: 'video' },
  { id: 'felles', name: 'Fælles', icon: 'circle-dot' },
];

/**
 * Privat-brugere ser mere brugervenlige navne for tekniske kategorier.
 * Brug getCategoryDisplayName(id, isPro) i UI i stedet for category.name direkte
 * når visningen kan ses af privat-brugere.
 */
const privateCategoryNameOverrides: Partial<Record<SymbolCategory, string>> = {
  aia: 'Alarm & overvågning',
};

export const getCategoryDisplayName = (
  category: SymbolCategoryInfo,
  isPro: boolean = false
): string => {
  if (isPro) return category.name;
  return privateCategoryNameOverrides[category.id] ?? category.name;
};

export const symbolSubcategories: SymbolSubcategoryInfo[] = [
  // Belysning
  { id: 'lighting_outlets', name: 'Udtag', parentCategory: 'lighting' },
  { id: 'lighting_fixtures', name: 'Armaturer', parentCategory: 'lighting' },
  { id: 'lighting_controls', name: 'Lysstyring', parentCategory: 'lighting' },
  
  // Afbrydere & Betjening
  { id: 'switches_standard', name: 'Standard afbrydere', parentCategory: 'switches' },
  { id: 'switches_push', name: 'Trykkontakter', parentCategory: 'switches' },
  { id: 'switches_special', name: 'Special afbrydere', parentCategory: 'switches' },
  { id: 'switches_sensors', name: 'Sensorer', parentCategory: 'switches' },
  
  // Tilslutninger
  { id: 'outlets_standard', name: 'Stikkontakter', parentCategory: 'outlets' },
  { id: 'outlets_connection', name: 'Tilslutningssteder', parentCategory: 'outlets' },
  { id: 'outlets_cee', name: 'CEE / Kraft', parentCategory: 'outlets' },
  
  // Data & Kommunikation
  { id: 'data_network', name: 'Netværk', parentCategory: 'data' },
  { id: 'data_tv', name: 'TV / Antenne', parentCategory: 'data' },
  
  // Forsyning & Installationer
  { id: 'power_panels', name: 'Tavler & Distribution', parentCategory: 'power' },
  { id: 'power_transformers', name: 'Transformere', parentCategory: 'power' },
  { id: 'power_solar', name: 'Solcelle', parentCategory: 'power' },
  { id: 'power_ev', name: 'El-bil', parentCategory: 'power' },
  { id: 'power_hvac', name: 'Ventilation', parentCategory: 'power' },
  { id: 'power_technical', name: 'Teknisk udstyr', parentCategory: 'power' },
  
  // Sikkerhed
  { id: 'safety_detectors', name: 'Detektorer', parentCategory: 'safety' },
  
  // AIA / Alarm & overvågning
  { id: 'aia_cameras', name: 'Kameraer', parentCategory: 'aia' },

  // Fælles (PRO-only)
  { id: 'felles_junction', name: 'Samledåser', parentCategory: 'felles' },
  { id: 'felles_wells', name: 'Brønde', parentCategory: 'felles' },
];

export const electricalSymbols: ElectricalSymbol[] = [
  // ============================================
  // BELYSNING - Udtag
  // ============================================
  {
    id: 'ceiling_outlet',
    type: 'ceiling_outlet',
    name: 'Lampeudtag',
    icon: 'ceiling_outlet',
    category: 'lighting',
    subcategory: 'lighting_outlets',
  },
  {
    id: 'wall_outlet',
    type: 'wall_outlet',
    name: 'Vægudtag',
    icon: 'wall_outlet',
    category: 'lighting',
    subcategory: 'lighting_outlets',
  },
  {
    id: 'wireless_ceiling_outlet',
    type: 'wireless_ceiling_outlet',
    name: 'Trådløs lampeudtag',
    icon: 'wireless_ceiling_outlet',
    category: 'lighting',
    subcategory: 'lighting_outlets',
  },
  {
    id: 'wireless_wall_outlet',
    type: 'wireless_wall_outlet',
    name: 'Trådløs vægudtag',
    icon: 'wireless_wall_outlet',
    category: 'lighting',
    subcategory: 'lighting_outlets',
  },
  
  // ============================================
  // BELYSNING - Armaturer
  // ============================================
  {
    id: 'lighting_fixture',
    type: 'lighting_fixture',
    name: 'Belysningsarmatur',
    icon: 'lighting_fixture',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  {
    id: 'lighting_fixture_pir',
    type: 'lighting_fixture_pir',
    name: 'Belysningsarmatur med PIR',
    icon: 'lighting_fixture_pir',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  {
    id: 'spot_light',
    type: 'spot_light',
    name: 'Spot',
    icon: 'spot_light',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  {
    id: 'wall_fixture',
    type: 'wall_fixture',
    name: 'Vægarmatur',
    icon: 'wall_fixture',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  {
    id: 'ceiling_lamp',
    type: 'ceiling_lamp',
    name: 'Plafond lampe',
    icon: 'ceiling_lamp',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  
  // ============================================
  // BELYSNING - Paneler
  // ============================================
  {
    id: 'panel_600',
    type: 'panel_600',
    name: 'Panel 600x600',
    icon: 'panel_600',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  
  {
    id: 'tube_short',
    type: 'tube_short',
    name: 'Lysrør kort',
    icon: 'tube_short',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  {
    id: 'pendant_wire',
    type: 'pendant_wire',
    name: 'Nedhængt i wire',
    icon: 'pendant_wire',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  {
    id: 'led_strip',
    type: 'led_strip',
    name: 'LED Strip',
    icon: 'led_strip',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  {
    id: 'projector',
    type: 'projector',
    name: 'Projektør',
    icon: 'projector',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  {
    id: 'projector_wall',
    type: 'projector_wall',
    name: 'Projektør vægmonteret',
    icon: 'projector_wall',
    category: 'lighting',
    subcategory: 'lighting_fixtures',
  },
  
  // ============================================
  // BELYSNING - Lysstyring
  // ============================================
  {
    id: 'lighting_central',
    type: 'lighting_central',
    name: 'Central for lysstyring',
    icon: 'lighting_central',
    category: 'lighting',
    subcategory: 'lighting_controls',
  },
  {
    id: 'dusk_relay',
    type: 'dusk_relay',
    name: 'Skumringsrelæ',
    icon: 'dusk_relay',
    category: 'lighting',
    subcategory: 'lighting_controls',
  },
  {
    id: 'pir_sensor',
    type: 'pir_sensor',
    name: 'PIR sensor 180°',
    icon: 'pir_sensor',
    category: 'lighting',
    subcategory: 'lighting_controls',
  },
  {
    id: 'pir_sensor_360',
    type: 'pir_sensor_360',
    name: 'PIR sensor 360°',
    icon: 'pir_sensor_360',
    category: 'lighting',
    subcategory: 'lighting_controls',
  },
  
  // ============================================
  // AFBRYDERE - Standard
  // ============================================
  {
    id: 'afbryder_1pol',
    type: 'afbryder_1pol',
    name: '1-polet afbryder',
    icon: 'afbryder_1pol',
    category: 'switches',
    subcategory: 'switches_standard',
  },
  {
    id: 'kroneafbryder',
    type: 'kroneafbryder',
    name: 'Kroneafbryder',
    icon: 'kroneafbryder',
    category: 'switches',
    subcategory: 'switches_standard',
  },
  {
    id: 'korrespondanceafbryder',
    type: 'korrespondanceafbryder',
    name: 'Korrespondanceafbryder',
    icon: 'korrespondanceafbryder',
    category: 'switches',
    subcategory: 'switches_standard',
  },
  {
    id: 'dobbelt_korrespondance',
    type: 'dobbelt_korrespondance',
    name: 'Dobbelt korrespondance',
    icon: 'dobbelt_korrespondance',
    category: 'switches',
    subcategory: 'switches_standard',
  },
  {
    id: 'krydsningsafbryder',
    type: 'krydsningsafbryder',
    name: 'Krydsningsafbryder',
    icon: 'krydsningsafbryder',
    category: 'switches',
    subcategory: 'switches_standard',
  },
  
  // ============================================
  // AFBRYDERE - Trykkontakter
  // ============================================
  {
    id: 'push_button',
    type: 'push_button',
    name: 'Trykkontakt',
    icon: 'push_button',
    category: 'switches',
    subcategory: 'switches_push',
  },
  {
    id: 'push_button_glow',
    type: 'push_button_glow',
    name: 'Trykkontakt m/ glimlampe',
    icon: 'push_button_glow',
    category: 'switches',
    subcategory: 'switches_push',
  },
  {
    id: 'wireless_push_button',
    type: 'wireless_push_button',
    name: 'Trådløs tryk',
    icon: 'wireless_push_button',
    category: 'switches',
    subcategory: 'switches_push',
  },
  
  // ============================================
  // AFBRYDERE - Special
  // ============================================
  {
    id: 'key_switch',
    type: 'key_switch',
    name: 'Nøgleafbryder',
    icon: 'key_switch',
    category: 'switches',
    subcategory: 'switches_special',
  },
  {
    id: 'safety_switch_16a',
    type: 'safety_switch_16a',
    name: 'Sikkerhedsafbryder 16A',
    icon: 'safety_switch_16a',
    category: 'switches',
    subcategory: 'switches_special',
  },
  {
    id: 'safety_switch_32a',
    type: 'safety_switch_32a',
    name: 'Sikkerhedsafbryder 32A',
    icon: 'safety_switch_32a',
    category: 'switches',
    subcategory: 'switches_special',
  },
  {
    id: 'safety_switch_63a',
    type: 'safety_switch_63a',
    name: 'Sikkerhedsafbryder 63A',
    icon: 'safety_switch_63a',
    category: 'switches',
    subcategory: 'switches_special',
  },
  
  // ============================================
  // AFBRYDERE - Sensorer (legacy - now empty, sensors moved to lighting_controls)
  // ============================================
  
  // ============================================
  // TILSLUTNINGER - Stikkontakter
  // ============================================
  {
    id: 'outlet',
    type: 'outlet',
    name: 'Stikkontakt',
    icon: 'outlet',
    category: 'outlets',
    subcategory: 'outlets_standard',
  },
  {
    id: 'outlet_switched',
    type: 'outlet_switched',
    name: 'Stikkontakt m/ afbryder',
    icon: 'outlet_switched',
    category: 'outlets',
    subcategory: 'outlets_standard',
  },
  {
    id: 'usb_outlet',
    type: 'usb_outlet',
    name: 'Stikkontakt med USB',
    icon: 'outlet', // Genbruger outlet-grafik; subLabel viser "USB" (inde i boksen, render-laget styrer placering så multi-outlet ikke giver "USBUSB")
    category: 'outlets',
    subcategory: 'outlets_standard',
    subLabel: 'USB',
  },
  {
    id: 'outlet_ungrounded',
    type: 'outlet_ungrounded',
    name: 'Stikkontakt uden jord',
    icon: 'outlet_ungrounded',
    category: 'outlets',
    subcategory: 'outlets_standard',
  },
  {
    id: 'schuko_outlet',
    type: 'schuko_outlet',
    name: 'Schuko-stikkontakt',
    icon: 'outlet', // Genbruger outlet-grafik; subLabel viser "schuko"
    category: 'outlets',
    subcategory: 'outlets_standard',
    subLabel: 'schuko',
  },
  
  // ============================================
  // TILSLUTNINGER - Tilslutningssteder
  // ============================================
  {
    id: 'connection_230v',
    type: 'connection_230v',
    name: 'Tilslutningssted 230V',
    icon: 'connection_230v',
    category: 'outlets',
    subcategory: 'outlets_connection',
  },
  {
    id: 'connection_400v',
    type: 'connection_400v',
    name: 'Tilslutningssted 400V',
    icon: 'connection_400v',
    category: 'outlets',
    subcategory: 'outlets_connection',
  },
  
  // ============================================
  // TILSLUTNINGER - CEE / Kraft
  // ============================================
  {
    id: 'cee_16a',
    type: 'cee_16a',
    name: 'CEE 16A',
    icon: 'cee_16a',
    category: 'outlets',
    subcategory: 'outlets_cee',
  },
  {
    id: 'cee_32a',
    type: 'cee_32a',
    name: 'CEE 32A',
    icon: 'cee_32a',
    category: 'outlets',
    subcategory: 'outlets_cee',
  },
  {
    id: 'cee_63a',
    type: 'cee_63a',
    name: 'CEE 63A',
    icon: 'cee_63a',
    category: 'outlets',
    subcategory: 'outlets_cee',
  },
  
  // ============================================
  // DATA - Netværk
  // ============================================
  {
    id: 'data_outlet',
    type: 'data_outlet',
    name: 'Dataudtag',
    icon: 'data_outlet',
    category: 'data',
    subcategory: 'data_network',
  },
  {
    id: 'access_point',
    type: 'access_point',
    name: 'Accesspoint',
    icon: 'access_point',
    category: 'data',
    subcategory: 'data_network',
  },
  
  // ============================================
  // DATA - TV
  // ============================================
  {
    id: 'tv_outlet',
    type: 'tv_outlet',
    name: 'TV-udtag',
    icon: 'tv_outlet',
    category: 'data',
    subcategory: 'data_tv',
  },
  {
    id: 'door_phone',
    type: 'door_phone',
    name: 'Dørtelefon',
    icon: 'door_phone',
    category: 'data',
    subcategory: 'data_network',
  },
  
  // ============================================
  // FORSYNING - Tavler & Distribution
  // ============================================
  {
    id: 'electrical_panel',
    type: 'electrical_panel',
    name: 'Eltavle',
    icon: 'electrical_panel',
    category: 'power',
    subcategory: 'power_panels',
  },
  {
    id: 'junction_box',
    type: 'junction_box',
    name: 'Krydsfelt',
    icon: 'krydsfelt',
    category: 'power',
    subcategory: 'power_panels',
  },
  {
    id: 'meter_frame',
    type: 'meter_frame',
    name: 'Målerramme',
    icon: 'meter_frame',
    category: 'power',
    subcategory: 'power_panels',
  },
  {
    id: 'grounding_terminal',
    type: 'grounding_terminal',
    name: 'Jordingsplint',
    icon: 'grounding_terminal',
    category: 'power',
    subcategory: 'power_panels',
  },
  
  // ============================================
  // FORSYNING - Transformere
  // ============================================
  {
    id: 'transformer',
    type: 'transformer',
    name: 'Transformer',
    icon: 'transformer',
    category: 'power',
    subcategory: 'power_transformers',
  },
  
  // ============================================
  // FORSYNING - Solcelle
  // ============================================
  {
    id: 'inverter_solar',
    type: 'inverter_solar',
    name: 'Solcelle inverter',
    icon: 'inverter_solar',
    category: 'power',
    subcategory: 'power_solar',
  },
  {
    id: 'solar_battery',
    type: 'solar_battery',
    name: 'Solcellebatteri',
    icon: 'solar_battery',
    category: 'power',
    subcategory: 'power_solar',
  },
  
  // ============================================
  // FORSYNING - El-bil
  // ============================================
  {
    id: 'ev_charger',
    type: 'ev_charger',
    name: 'El-bil lader',
    icon: 'ev_charger',
    category: 'power',
    subcategory: 'power_ev',
  },
  
  // ============================================
  // FORSYNING - Ventilation
  // ============================================
  {
    id: 'ventilator',
    type: 'ventilator',
    name: 'Ventilator',
    icon: 'ventilator',
    category: 'power',
    subcategory: 'power_hvac',
  },
  {
    id: 'floor_heating_thermostat',
    type: 'floor_heating_thermostat',
    name: 'Termostat el-gulvvarme',
    icon: 'floor_heating_thermostat',
    category: 'power',
    subcategory: 'power_hvac',
  },
  {
    id: 'heating_surface',
    type: 'heating_surface',
    name: 'Varmeflade',
    icon: 'heating_surface',
    category: 'power',
    subcategory: 'power_hvac',
  },
  {
    id: 'heat_pump_indoor',
    type: 'heat_pump_indoor',
    name: 'Varmepumpe indedel',
    icon: 'heat_pump_indoor',
    category: 'power',
    subcategory: 'power_hvac',
  },
  {
    id: 'heat_pump_outdoor',
    type: 'heat_pump_outdoor',
    name: 'Varmepumpe udedel',
    icon: 'heat_pump_outdoor',
    category: 'power',
    subcategory: 'power_hvac',
  },
  
  // ============================================
  // FORSYNING - Teknisk udstyr
  // ============================================
  {
    id: 'hand_dryer',
    type: 'hand_dryer',
    name: 'Håndtørrer',
    icon: 'hand_dryer',
    category: 'power',
    subcategory: 'power_technical',
  },
  
  // ============================================
  // SIKKERHED - Detektorer
  // ============================================
  {
    id: 'smoke_detector',
    type: 'smoke_detector',
    name: 'Røgdetektor',
    icon: 'smoke_detector',
    category: 'safety',
    subcategory: 'safety_detectors',
  },
  
  // ============================================
  // AIA / ALARM & OVERVÅGNING - Kameraer
  // ============================================
  {
    id: 'video_camera',
    type: 'video_camera',
    name: 'Videokamera',
    icon: 'video_camera',
    category: 'aia',
    subcategory: 'aia_cameras',
  },

  // ============================================
  // FÆLLES - Samledåser (PRO-only)
  // ============================================
  {
    id: 'samledaase',
    type: 'samledaase',
    name: 'Samledåse',
    icon: 'samledaase',
    category: 'felles',
    subcategory: 'felles_junction',
    proOnly: true,
  },
  {
    id: 'teknikbroend',
    type: 'teknikbroend',
    name: 'Teknikbrønd',
    icon: 'teknikbroend',
    category: 'felles',
    subcategory: 'felles_wells',
    proOnly: true,
  },
];

export const getSymbolsByCategory = (category: SymbolCategory): ElectricalSymbol[] => {
  return electricalSymbols.filter(s => s.category === category);
};

export const getSymbolsBySubcategory = (subcategory: SymbolSubcategory): ElectricalSymbol[] => {
  return electricalSymbols.filter(s => s.subcategory === subcategory);
};

export const getSubcategoriesByCategory = (category: SymbolCategory): SymbolSubcategoryInfo[] => {
  return symbolSubcategories.filter(sc => sc.parentCategory === category);
};

export const getSymbolById = (id: string): ElectricalSymbol | undefined => {
  return electricalSymbols.find(s => s.id === id);
};

/**
 * Slå katalog-subLabel op for en symboltype.
 * Returnerer fx 'schuko' for `schuko_outlet`. Bruges af canvas + PDF
 * til at rendere én lille tekst under symbolet (uafhængigt af outletCount).
 */
export const getSymbolSubLabel = (type: SymbolType): string | undefined => {
  return electricalSymbols.find(s => s.type === type)?.subLabel;
};
